import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Merch.css';
import ShoppingCart from '../components/ShoppingCart';
import SizeSelectionModal from '../components/SizeSelectionModal';

const Merch = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showSizeModal, setShowSizeModal] = useState(false);

  // Calculate total items in cart
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  // Toggle cart visibility
  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  // Handle add to cart button click
  const handleAddToCartClick = (product) => {
    console.log('Adding to cart:', product);
    // Check if it's clothing and needs size selection
    if (product.category === 'clothing') {
      setSelectedProduct(product);
      setShowSizeModal(true);
    } else {
      // For non-clothing items, add directly to cart
      addToCart(product);
    }
  };

  // Add item to cart
  const addToCart = (product, size = null) => {
    console.log('Adding to cart with size:', product, size);
    setCartItems(prevItems => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(item => 
        size ? (item.id === product.id && item.size === size) : (item.id === product.id && !item.size)
      );
      
      if (existingItemIndex >= 0) {
        // Item exists, update quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += 1;
        return updatedItems;
      } else {
        // Item doesn't exist, add new item
        return [...prevItems, {
          ...product,
          size,
          quantity: 1
        }];
      }
    });
  };
  
  // Add to cart with selected size
  const addToCartWithSize = (product, size) => {
    addToCart(product, size);
  };
  
  // Remove item from cart
  const removeFromCart = (productId, size = null) => {
    setCartItems(prevItems => 
      prevItems.filter(item => 
        size ? !(item.id === productId && item.size === size) : !(item.id === productId && !item.size)
      )
    );
  };
  
  // Update quantity of an item in cart
  const updateQuantity = (productId, newQuantity, size = null) => {
    if (newQuantity <= 0) {
      removeFromCart(productId, size);
      return;
    }
    
    setCartItems(prevItems => {
      return prevItems.map(item => {
        if (size) {
          // For items with size
          if (item.id === productId && item.size === size) {
            return { ...item, quantity: newQuantity };
          }
        } else {
          // For items without size
          if (item.id === productId && !item.size) {
            return { ...item, quantity: newQuantity };
          }
        }
        return item;
      });
    });
  };
  
  // Save cart to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
    console.log('Cart updated:', cartItems);
  }, [cartItems]);

  // Load cart from localStorage on component mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);
  
  // Update global cart count
  useEffect(() => {
    const cartCountElement = document.getElementById('cart-count');
    if (cartCountElement) {
      cartCountElement.textContent = cartItemCount.toString();
    }
    
    // Set up click handler for global cart icon
    const globalCart = document.getElementById('global-cart');
    if (globalCart) {
      globalCart.onclick = toggleCart;
    }
  }, [cartItemCount, toggleCart]);

  // Sample products data
  const products = [
    {
      id: 1,
      name: "Black Cat Logo T-Shirt",
      price: 24.99,
      category: "clothing",
      description: "Soft cotton t-shirt with the Black Cat Ghost Hunting logo.",
      image: "/images/tshirt.jpg"
    },
    {
      id: 2,
      name: "Black Cat Hoodie",
      price: 39.99,
      category: "clothing",
      description: "Stay warm on those cold investigation nights.",
      image: "/images/hoodie.jpg"
    },
    {
      id: 3,
      name: "Black Cat Mug",
      price: 14.99,
      category: "accessories",
      description: "Perfect for your midnight coffee during ghost watches.",
      image: "/images/mug.jpg"
    },
    {
      id: 4,
      name: "Black Cat Beanie",
      price: 19.99,
      category: "accessories",
      description: "Stylish beanie with embroidered ghost logo.",
      image: "/images/beanie.jpg"
    },
    {
      id: 5,
      name: "Black Cat Sticker Pack",
      price: 9.99,
      category: "accessories",
      description: "Set of 5 vinyl stickers featuring our ghost designs.",
      image: "/images/stickers.jpg"
    }
  ];

  // Filter products by category
  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(product => product.category === activeCategory);

  return (
    <div className="merch">
      {/* Shopping Cart Sidebar - Rendered in portal */}
      {isCartOpen && ReactDOM.createPortal(
        <ShoppingCart 
          cartItems={cartItems} 
          removeFromCart={removeFromCart} 
          updateQuantity={updateQuantity} 
          closeCart={() => setIsCartOpen(false)} 
        />,
        document.getElementById('shopping-cart-container') || document.body
      )}
      
      {/* Size Selection Modal - Rendered in portal */}
      {showSizeModal && selectedProduct && ReactDOM.createPortal(
        <SizeSelectionModal 
          product={selectedProduct} 
          onAddToCart={addToCartWithSize} 
          onClose={() => setShowSizeModal(false)} 
        />,
        document.getElementById('size-modal-container') || document.body
      )}
      
      <div className="merch-header">
        <h1>Ghost Hunting Merchandise</h1>
        <p>Show your support with our official Black Cat Ghost Hunting gear.</p>
      </div>
      
      <div className="category-filter">
        <button 
          className={activeCategory === 'all' ? 'active' : ''} 
          onClick={() => setActiveCategory('all')}
        >
          All Items
        </button>
        <button 
          className={activeCategory === 'clothing' ? 'active' : ''} 
          onClick={() => setActiveCategory('clothing')}
        >
          Clothing
        </button>
        <button 
          className={activeCategory === 'accessories' ? 'active' : ''} 
          onClick={() => setActiveCategory('accessories')}
        >
          Accessories
        </button>
      </div>
      
      <div className="products-grid">
        {filteredProducts.map(product => (
          <div className="product-card" key={product.id}>
            <div className="product-image">
              <img src={product.image} alt={product.name} />
            </div>
            <div className="product-info">
              <h3>{product.name}</h3>
              <p className="product-price">${product.price.toFixed(2)}</p>
              <p className="product-description">{product.description}</p>
              <button 
                className="add-to-cart-btn"
                onClick={() => handleAddToCartClick(product)}
              >
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Merch;
